import { z } from 'zod'
import {
  DELIVERY_PICKUP,
  DELIVERY_SHIP,
  HISTORY_ACTIONS,
  MAX_LINES_PER_ORDER,
  MAX_QTY_PER_LINE,
  ORDER_STATUSES,
  PAYMENT_MODES,
  SHIPPING_CARRIERS,
} from '../constants.js'
import { zBaht, zDate, zEmail, zPhone, zPostcode } from '../zod-utils.js'

export const ShippingAddressSchema = z.object({
  recipient: z.string().trim().min(1, 'กรุณากรอกชื่อผู้รับ').max(160),
  addressLine: z.string().trim().min(5, 'กรุณากรอกที่อยู่ (บ้านเลขที่ ถนน)').max(400),
  subdistrict: z.string().trim().min(1, 'กรุณาเลือกตำบล/แขวง').max(120),
  district: z.string().trim().min(1, 'กรุณาเลือกอำเภอ/เขต').max(120),
  province: z.string().trim().min(1, 'กรุณาเลือกจังหวัด').max(120),
  postcode: zPostcode,
})
export type ShippingAddress = z.infer<typeof ShippingAddressSchema>

export const DeliverySchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal(DELIVERY_PICKUP) }),
  ShippingAddressSchema.extend({ type: z.literal(DELIVERY_SHIP) }),
])
export type Delivery = z.infer<typeof DeliverySchema>

/** What the client asks for. Notice there is no price anywhere in here. */
export const OrderLineInputSchema = z.object({
  sku: z.string().trim().min(1),
  /**
   * Absent when the item has no colourways at all (a fee, a membership). Orders
   * placed before colours existed also have none, which is why this is optional
   * rather than defaulted — a missing colour is a real state, not a blank one.
   */
  color: z.string().trim().min(1).optional(),
  size: z.string().trim().min(1),
  qty: z.number().int().min(1).max(MAX_QTY_PER_LINE),
})
export type OrderLineInput = z.infer<typeof OrderLineInputSchema>

export const CreateOrderInputSchema = z.object({
  participantId: z.string().trim().min(1, 'กรุณาเลือกชื่อผู้สั่ง'),
  email: zEmail,
  phone: zPhone,
  delivery: DeliverySchema,
  lines: z
    .array(OrderLineInputSchema)
    .min(1, 'กรุณาเลือกสินค้าอย่างน้อย 1 รายการ')
    .max(MAX_LINES_PER_ORDER, `สั่งได้ไม่เกิน ${MAX_LINES_PER_ORDER} รายการต่อออเดอร์`),
  /** Event-specific fields, validated by `functions/src/event/extraSchema.ts`. */
  extra: z.record(z.unknown()).optional(),
  acceptedTerms: z.literal(true, {
    errorMap: () => ({ message: 'ต้องยอมรับเงื่อนไขพรีออเดอร์ก่อนชำระเงิน' }),
  }),
})
export type CreateOrderInput = z.infer<typeof CreateOrderInputSchema>

/** A line after the server has priced it. `unitPrice` is a snapshot, never re-derived. */
export const OrderLineSchema = OrderLineInputSchema.extend({
  name: z.string().trim().min(1),
  /** Snapshot of the colour's display name, so renaming a colour later cannot rewrite history. */
  colorName: z.string().trim().optional(),
  unitPrice: zBaht,
  lineTotal: zBaht,
})
export type OrderLine = z.infer<typeof OrderLineSchema>

export const PaymentRefSchema = z.object({
  provider: z.string().min(1),
  mode: z.enum(PAYMENT_MODES),
  /** Provider-side charge / payment-link id. */
  ref: z.string().nullable(),
  /** `redirect` mode only. */
  url: z.string().url().nullable().optional(),
  /** `qr` mode only. */
  qr: z
    .object({
      /**
       * Either is enough, and neither is guaranteed: Beam normally returns the PNG,
       * while `rawData` is the EMVCo payload the page can draw itself. Requiring the
       * PNG specifically would reject a perfectly payable QR.
       */
      imageBase64: z.string(),
      rawData: z.string(),
      expiresAt: zDate.nullable(),
    })
    .refine((qr) => qr.imageBase64.length > 0 || qr.rawData.length > 0, {
      message: 'ไม่มีข้อมูล QR สำหรับชำระเงิน',
    })
    .nullable()
    .optional(),
})
export type PaymentRef = z.infer<typeof PaymentRefSchema>

/**
 * The parcel number, once there is one.
 *
 * `notifiedAt` is the proof the buyer was told, and it is what stops a second
 * click from sending a second "your parcel is on its way" for the same parcel.
 */
export const TrackingSchema = z.object({
  carrier: z.enum(SHIPPING_CARRIERS),
  number: z.string().trim().min(4, 'เลขพัสดุสั้นเกินไป').max(40, 'เลขพัสดุยาวเกินไป'),
  notifiedAt: zDate.nullable().optional(),
})
export type Tracking = z.infer<typeof TrackingSchema>

export const OrderSchema = z.object({
  oid: z.string().min(1),
  participantId: z.string().min(1),
  participantName: z.string().min(1),
  email: zEmail,
  phone: z.string().min(9),
  delivery: DeliverySchema,
  items: z.array(OrderLineSchema).min(1),
  extra: z.record(z.unknown()).optional(),
  itemCount: z.number().int().min(1),
  subtotal: zBaht,
  /** Tax on the shirts, added on top. Absent on orders placed before it existed. */
  vat: zBaht.default(0),
  shippingFee: zBaht,
  amount: zBaht,
  acceptedTerms: z.literal(true),
  status: z.enum(ORDER_STATUSES),
  payment: PaymentRefSchema,
  createdAt: zDate,
  /** When a still-`pending` order stops being payable. Drives `expirePendingOrders`. */
  expiresAt: zDate.nullable().optional(),
  paidAt: zDate.nullable().optional(),
  shippedAt: zDate.nullable().optional(),
  /** When the organiser confirmed the buyer actually has it. Terminal. */
  receivedAt: zDate.nullable().optional(),
  /** When the organiser called it off. Terminal. */
  cancelledAt: zDate.nullable().optional(),
  /** Set when the organiser hands over the parcel number. See TrackingSchema. */
  tracking: TrackingSchema.nullable().optional(),
  expiredAt: zDate.nullable().optional(),
})
export type Order = z.infer<typeof OrderSchema>

export const HistoryEntrySchema = z.object({
  at: zDate,
  /** An admin e-mail, or one of SYSTEM_ACTORS. */
  actor: z.string().min(1),
  action: z.enum(HISTORY_ACTIONS),
  from: z.string().optional(),
  to: z.string().optional(),
  note: z.string().optional(),
})
export type HistoryEntry = z.infer<typeof HistoryEntrySchema>

/** What `getOrderStatus` hands back to the buyer's polling page. */
export const OrderStatusResultSchema = z.object({
  oid: z.string(),
  status: z.enum(ORDER_STATUSES),
  amount: zBaht,
  /** Masked — the page confirms which inbox to check without revealing it. */
  emailMasked: z.string(),
  /**
   * What was bought. The result page is where a buyer waits with their banking app
   * open, and a total with no line items is the one place they cannot check they
   * ordered the right size before paying.
   */
  items: z.array(OrderLineSchema),
  /*
   * Sent, not inferred.
   *
   * The result page used to derive the delivery charge as `amount - sum(lines)`,
   * which was exactly right until tax existed and then silently reported "ค่าส่ง"
   * as shipping PLUS VAT. A figure the server already knows should never be
   * reconstructed by subtraction on the client.
   */
  vat: zBaht.default(0),
  shippingFee: zBaht.default(0),
  delivery: DeliverySchema,
  paidAt: zDate.nullable(),
  /** Present only while `pending`, so a reloaded page can still show the QR. */
  payment: PaymentRefSchema.nullable(),
  /**
   * Shown once the parcel is on its way. Safe here: reaching this page already
   * needs the order id, and the number is printed on the outside of the box.
   */
  tracking: TrackingSchema.nullable(),
})
export type OrderStatusResult = z.infer<typeof OrderStatusResultSchema>

export const LookupOrderInputSchema = z.object({
  oid: z.string().trim().min(4).max(32),
})

export const AdminUpdateOrderInputSchema = z.discriminatedUnion('action', [
  z.object({
    action: z.literal('change_size'),
    oid: z.string().trim().min(1),
    lineIndex: z.number().int().min(0),
    toSize: z.string().trim().min(1),
    /** Optional: swap colour at the same time, if it costs the same. */
    toColor: z.string().trim().min(1).optional(),
    note: z.string().trim().max(500).optional(),
  }),
  z.object({
    action: z.literal('mark_shipped'),
    oid: z.string().trim().min(1),
    /**
     * Recording the number and telling the buyer are separate decisions: a batch of
     * parcels gets its numbers entered long before anyone wants twenty e-mails to go
     * out, and an organiser fixing a typo should not re-notify.
     */
    notify: z.boolean().default(true),
    /**
     * Optional because a pickup order still gets marked handed-over, and it has no
     * parcel. When it IS present the buyer is e-mailed the number.
     */
    tracking: TrackingSchema.omit({ notifiedAt: true }).optional(),
    note: z.string().trim().max(500).optional(),
  }),
  /*
   * Support: the details a buyer typed wrong.
   *
   * Everything here is free of money. The amount on a paid order is what Beam
   * actually charged, and the moment an edit can change it the books stop agreeing
   * with the bank — which is why sizes may only be swapped for ones that cost the
   * same, and why the delivery TYPE is not editable here: pickup and shipping differ by
   * the shipping fee. Changing what was charged is a refund, not an edit.
   *
   * Every field optional: the form sends only what the organiser actually touched,
   * so two people fixing different things cannot overwrite each other.
   */
  z.object({
    action: z.literal('edit_details'),
    oid: z.string().trim().min(1),
    participantName: z.string().trim().min(1, 'ชื่อห้ามว่าง').max(160).optional(),
    email: zEmail.optional(),
    phone: zPhone.optional(),
    /** Address only. A pickup order has nothing here and stays pickup. */
    address: ShippingAddressSchema.optional(),
    extra: z.record(z.unknown()).optional(),
    note: z.string().trim().max(500).optional(),
  }),
  z.object({
    action: z.literal('mark_received'),
    oid: z.string().trim().min(1),
    note: z.string().trim().max(500).optional(),
  }),
  z.object({
    action: z.literal('cancel'),
    oid: z.string().trim().min(1),
    /**
     * Required, not optional.
     *
     * Cancelling is the one admin action with no automatic trace of WHY — a refund
     * happened outside the system, or a buyer asked, or it was a duplicate. Six
     * months later the history is the only place that answer exists, and "cancelled
     * by someone, at some point" is not an answer.
     */
    note: z.string().trim().min(3, 'กรุณาระบุเหตุผลที่ยกเลิก').max(500),
  }),
])
export type AdminUpdateOrderInput = z.infer<typeof AdminUpdateOrderInputSchema>
